import React from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarDays } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import {
  InsightsBaseProps,
  NEUTRAL_TONE,
  formatShortDate,
  formatWeekday,
  getDayScore,
  getDayTone,
  hasCalendarScore,
} from './insightsCalendarUtils';

type WeeklyStripProps = InsightsBaseProps;

// Faixa semanal partilhada entre Lite e Pro: um cartao por dia com melhor/pior
// perfil e score (fonte visual unica).
const WeeklyStrip: React.FC<WeeklyStripProps> = ({
  days,
  titleKey = 'insightsLiteWeeklyTitle',
  descriptionKey = 'insightsLiteWeeklySubtitle',
  children,
}) => {
  const { i18n, t } = useTranslation();

  return (
    <>
    <Card className="border-stone-200 bg-white shadow-sm transition-shadow hover:shadow-md">
      <CardHeader>
        <CardTitle className="flex items-center text-slate-800">
          <CalendarDays className="mr-2 h-5 w-5 text-blue-600" />
          {t(titleKey)}
        </CardTitle>
        <CardDescription>{t(descriptionKey)}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-2 md:grid md:grid-cols-7 md:gap-3">
          {days.map((day, index) => {
            const dataAvailable = hasCalendarScore(day);
            const score = getDayScore(day);
            const tone = getDayTone(score);
            const ToneIcon = tone.icon;
            const border = dataAvailable ? tone.border : NEUTRAL_TONE.border;
            const bg = dataAvailable ? tone.bg : NEUTRAL_TONE.bg;
            const text = dataAvailable ? tone.text : NEUTRAL_TONE.text;

            return (
              <div
                key={day.date?.toISOString() ?? day.bestProfileKey ?? index}
                className={`flex items-center gap-3 rounded-md border p-3 md:block md:min-h-[150px] ${border} ${bg}`}
              >
                <div className="flex w-14 shrink-0 flex-col md:w-auto md:flex-row md:items-center md:justify-between md:gap-2">
                  <div>
                    <p className="text-xs font-semibold uppercase text-slate-500">
                      {formatWeekday(day.date, i18n.language)}
                    </p>
                    <p className="text-sm font-bold text-slate-900">{formatShortDate(day.date, i18n.language)}</p>
                  </div>
                  <ToneIcon className={`hidden h-4 w-4 shrink-0 md:block ${text}`} />
                </div>
                <div className="flex min-w-0 flex-1 items-center justify-between gap-3 md:mt-3 md:block md:text-xs">
                  <div className="min-w-0 space-y-1 text-xs md:space-y-2">
                    <p className={`font-semibold ${text}`}>{dataAvailable ? t(tone.labelKey) : '-'}</p>
                    <p className="truncate text-slate-700">
                      <span className="font-medium">{t('insightsLiteBestProfile')}:</span>{' '}
                      {day.bestProfileKey ? t('profile_' + day.bestProfileKey) : '-'}
                    </p>
                    <p className="truncate text-slate-700">
                      <span className="font-medium">{t('insightsLiteCautionProfile')}:</span>{' '}
                      {day.cautionProfileKey ? t('profile_' + day.cautionProfileKey) : '-'}
                    </p>
                  </div>
                  <p className="shrink-0 text-base font-bold text-slate-900 md:mt-2 md:text-sm">
                    {dataAvailable ? `${score}%` : '-'}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
    {children}
    </>
  );
};

export default WeeklyStrip;
